import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  DocumentTextIcon,
  BuildingOffice2Icon,
  IdentificationIcon,
  InformationCircleIcon,
  MapPinIcon,
  TagIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

import { fetchPayments } from '../../../redux/actions/payments';
import {
  fetchBrandBillingDetails,
  saveBrandBillingDetails,
} from '../../../redux/actions/brands';
import { computeT2bFeeInvoiceFromTotal } from '../../../utils/bookingPricing';
import {
  EASYPAY_LOGIN_URL,
  EASYPAY_CHOICE,
  hasEasypayPayoutAccount,
  hasDeclaredEasypaySignup,
} from '../../../utils/easypayPayout';
import { updatePromoter } from '../../../redux/actions/promoter';
import useEasypayGate from 'hooks/useEasypayGate';
import {
  PayoutJourney,
  EasypayConnectButton,
  EasypayCopy,
  HowYouGetPaidModal,
} from 'components/EasypayPayout';
import api from '../../../utils/axiosConfig';

/* ───────────────────────── shared atoms (local) ───────────────────────────── */

const Eyebrow = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.14em] text-muted font-bold mb-1.5">
    {children}
  </div>
);

const TabHeader = ({ eyebrow, title, sub, actions }) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-6 mb-6 pb-5 border-b border-line">
    <div className="min-w-0">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="text-[26px] sm:text-[30px] md:text-[34px] leading-[1.05] tracking-tight text-ink-strong font-bold break-words">
        {title}
      </h2>
      {sub && (
        <p className="text-[14px] text-muted mt-2 max-w-xl leading-relaxed">
          {sub}
        </p>
      )}
    </div>
    {actions && (
      <div className="flex items-center gap-2 flex-wrap">{actions}</div>
    )}
  </div>
);

const Card = ({ children, className = '', padded = true }) => (
  <section
    className={`bg-white border border-line rounded-2xl shadow-softer ${
      padded ? 'p-6' : ''
    } ${className}`}
  >
    {children}
  </section>
);

const CardHeader = ({ eyebrow, title, sub, action }) => (
  <header className="flex items-start justify-between gap-4 mb-5">
    <div className="min-w-0">
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h3 className="text-[17px] sm:text-[19px] font-bold tracking-tight text-ink-strong leading-snug">
        {title}
      </h3>
      {sub && <p className="text-sm text-muted mt-1 max-w-prose">{sub}</p>}
    </div>
    {action}
  </header>
);

/* ─────────────────────────── status pill ─────────────────────────────────── */

const STATUS_MAP = {
  paid: { color: 'emerald', key: 'paid' },
  completed: { color: 'emerald', key: 'paid' },
  success: { color: 'emerald', key: 'paid' },
  pending: { color: 'amber', key: 'pending' },
  processing: { color: 'amber', key: 'pending' },
  failed: { color: 'rose', key: 'failed' },
  rejected: { color: 'rose', key: 'failed' },
  declined: { color: 'rose', key: 'failed' },
  refunded: { color: 'violet', key: 'refunded' },
};

const COLOR_CLASSES = {
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-800', dot: 'bg-amber-500' },
  rose: { bg: 'bg-rose-50', text: 'text-rose-700', dot: 'bg-rose-500' },
  violet: { bg: 'bg-violet-50', text: 'text-violet-700', dot: 'bg-violet-500' },
  neutral: { bg: 'bg-paper-2', text: 'text-ink', dot: 'bg-muted' },
};

const StatusPill = ({ status }) => {
  const { t } = useTranslation();
  const key = String(status || '').toLowerCase();
  const cfg = STATUS_MAP[key] || { color: 'neutral', key: 'unknown' };
  const c = COLOR_CLASSES[cfg.color] || COLOR_CLASSES.neutral;
  const label = t(
    `dashboard.payment_details_page.status.${cfg.key}`,
    status || ''
  );
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full text-[11.5px] font-bold px-2.5 py-0.5 ${c.bg} ${c.text}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {label}
    </span>
  );
};

/* ──────────────── billing details (promoter: read-only cells) ────────────── */

const InfoCell = ({ icon: Icon, label, value, mono }) => {
  const empty = !value;
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="w-9 h-9 rounded-lg bg-brand-softer flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-yellow-03" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10.5px] uppercase tracking-[0.08em] text-yellow-03 font-bold">
          {label}
        </div>
        <div
          className={`text-[13.5px] truncate ${
            empty ? 'text-muted italic' : 'text-ink'
          } ${mono ? 'font-mono' : ''}`}
          title={value || ''}
        >
          {value || '—'}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────── billing details (brand: editable) ───────────────────── */

const BILLING_FIELDS = ['name', 'nif', 'address', 'contact'];

const toBillingForm = (details) => ({
  name: details?.name || '',
  nif: details?.nif || '',
  address: details?.address || '',
  contact: details?.contact || '',
});

const Field = ({
  label,
  required,
  error,
  value,
  onChange,
  placeholder,
  mono,
  ...inputProps
}) => (
  <label className="block space-y-1.5">
    <span className="flex items-center justify-between gap-2">
      <span className="text-[10.5px] uppercase tracking-[0.08em] text-yellow-03 font-bold">
        {label}
      </span>
      {required && (
        <span className="text-[10px] uppercase tracking-[0.08em] font-bold text-rose-500">
          *
        </span>
      )}
    </span>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-invalid={!!error}
      className={`w-full h-11 px-3 rounded-xl border bg-white text-[14px] text-ink outline-none transition placeholder:text-muted/70 focus:border-yellow-03 focus:ring-2 focus:ring-yellow-03/25 ${
        mono ? 'font-mono' : ''
      } ${error ? 'border-rose-300' : 'border-line'}`}
      {...inputProps}
    />
    {error && <span className="block text-[12px] text-rose-600">{error}</span>}
  </label>
);

/**
 * The brand's reusable invoicing identity. Saved here once and used to
 * pre-fill the checkout billing step, so the same three fields don't have to
 * be retyped for every booking. Editing happens in place — the fields the
 * invoice actually needs (name / tax id / address) are the only ones shown;
 * country, city and email belong to the profile, not to invoicing.
 *
 * Saving only changes the default: bookings already paid keep the snapshot
 * taken at their own checkout, which is what their invoices were issued
 * against.
 */
const BrandBillingCard = ({ t }) => {
  const dispatch = useDispatch();
  const {
    billingDetails,
    billingDetailsLoaded,
    billingDetailsLoading,
    billingDetailsSaving,
    billingDetailsError,
  } = useSelector((s) => s.brands);

  const [form, setForm] = useState(() => toBillingForm(billingDetails));
  const [errors, setErrors] = useState({});
  const [saved, setSaved] = useState(false);
  // Typing before the initial fetch lands must not be overwritten by it.
  const editedRef = useRef(false);

  useEffect(() => {
    dispatch(fetchBrandBillingDetails());
  }, [dispatch]);

  // Adopt the server value once it lands (and after a save round-trips the
  // normalised version back: trimmed strings, nulls for blank optionals).
  useEffect(() => {
    if (editedRef.current) return;
    setForm(toBillingForm(billingDetails));
    setErrors({});
  }, [billingDetails]);

  const update = (field) => (value) => {
    editedRef.current = true;
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setSaved(false);
  };

  const persisted = toBillingForm(billingDetails);
  const isDirty = BILLING_FIELDS.some((k) => form[k].trim() !== persisted[k]);
  const isEmptyForm = Object.values(form).every((v) => !v.trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (billingDetailsSaving) return;

    // All-blank is a legitimate "clear my saved details"; anything else has to
    // carry name + tax id, since that's the minimum checkout will accept.
    const nextErrors = {};
    if (!isEmptyForm) {
      if (!form.name.trim()) {
        nextErrors.name = t('dashboard.payment_details_page.field_required');
      }
      if (!form.nif.trim()) {
        nextErrors.nif = t('dashboard.payment_details_page.field_required');
      }
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    try {
      // Release the "don't clobber my typing" guard before dispatching: what
      // comes back IS what we're sending, normalised, and it should win.
      editedRef.current = false;
      await dispatch(
        saveBrandBillingDetails(
          isEmptyForm
            ? null
            : {
                name: form.name.trim(),
                nif: form.nif.trim(),
                address: form.address.trim() || null,
                contact: form.contact.trim() || null,
              }
        )
      );
      setSaved(true);
    } catch (_) {
      // billingDetailsError carries the message; the form keeps what was typed
      editedRef.current = true;
    }
  };

  return (
    <Card>
      <form onSubmit={handleSubmit} noValidate>
        <CardHeader
          eyebrow={t('dashboard.payment_details_page.billing_eyebrow')}
          title={t('dashboard.payment_details_page.billing_title_brand')}
          sub={t('dashboard.payment_details_page.billing_hint')}
          action={
            billingDetailsLoading && !billingDetailsLoaded ? (
              <ClipLoader color="#FFBD59" size={18} />
            ) : null
          }
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label={t('dashboard.payment_details_page.field_name')}
            required
            value={form.name}
            onChange={update('name')}
            error={errors.name}
            placeholder={t('checkout.billing_name_placeholder')}
            autoComplete="name"
          />
          <Field
            label={t('dashboard.payment_details_page.field_nif')}
            required
            mono
            value={form.nif}
            onChange={update('nif')}
            error={errors.nif}
            placeholder={t('checkout.billing_nif_placeholder')}
            inputMode="numeric"
            autoComplete="off"
          />
          <Field
            label={t('dashboard.payment_details_page.field_address')}
            value={form.address}
            onChange={update('address')}
            placeholder={t('checkout.billing_address_placeholder')}
            autoComplete="street-address"
          />
          <Field
            label={t('dashboard.payment_details_page.field_contact')}
            value={form.contact}
            onChange={update('contact')}
            placeholder={t('checkout.billing_mobile_placeholder')}
            inputMode="tel"
            autoComplete="tel"
          />
        </div>

        {billingDetailsError && (
          <p className="mt-4 rounded-xl bg-rose-50 border border-rose-100 px-3 py-2.5 text-[13px] text-rose-700">
            {billingDetailsError}
          </p>
        )}

        <div className="mt-5 flex items-center justify-end gap-3">
          {saved && !isDirty && (
            <span className="inline-flex items-center gap-1.5 text-[13px] font-bold text-emerald-700">
              <CheckCircleIcon className="w-4 h-4" />
              {t('dashboard.payment_details_page.billing_saved')}
            </span>
          )}
          <button
            type="submit"
            disabled={billingDetailsSaving || !isDirty}
            className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-yellow-03 text-ink-strong text-[13.5px] font-bold hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {billingDetailsSaving && <ClipLoader color="#1A1A1F" size={13} />}
            {t('dashboard.payment_details_page.billing_save')}
          </button>
        </div>
      </form>
    </Card>
  );
};

const PromoterBillingCard = ({ promoter, t, onNavigateToInfo }) => {
  const hasAny =
    !!promoter?.companyName ||
    !!promoter?.fiscalNumber ||
    !!promoter?.firm ||
    !!promoter?.headquarters ||
    !!promoter?.country ||
    !!promoter?.city;

  return (
    <Card>
      <CardHeader
        eyebrow={t('dashboard.payment_details_page.billing_eyebrow')}
        title={t('dashboard.payment_details_page.billing_title_promoter')}
        sub={t('dashboard.payment_details_page.billing_hint_promoter')}
        action={
          onNavigateToInfo ? (
            <button
              type="button"
              onClick={onNavigateToInfo}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-ink hover:bg-paper-2 text-[13px] font-bold"
            >
              {t('dashboard.edit_profile')}
              <ArrowTopRightOnSquareIcon className="w-4 h-4" />
            </button>
          ) : null
        }
      />
      {hasAny ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1">
          <InfoCell
            icon={BuildingOffice2Icon}
            label={t('dashboard.payment_details_page.field_company')}
            value={promoter.companyName}
          />
          <InfoCell
            icon={IdentificationIcon}
            label={t('dashboard.payment_details_page.field_nif')}
            value={promoter.fiscalNumber}
            mono
          />
          <InfoCell
            icon={TagIcon}
            label={t('dashboard.payment_details_page.field_firm')}
            value={promoter.firm}
          />
          <InfoCell
            icon={MapPinIcon}
            label={t('dashboard.payment_details_page.field_headquarters')}
            value={promoter.headquarters}
          />
          <InfoCell
            icon={MapPinIcon}
            label={t('dashboard.payment_details_page.field_country')}
            value={promoter.country}
          />
          <InfoCell
            icon={MapPinIcon}
            label={t('dashboard.payment_details_page.field_city')}
            value={promoter.city}
          />
        </div>
      ) : (
        <p className="text-[13.5px] text-muted italic">
          {t('dashboard.payment_details_page.billing_empty')}
        </p>
      )}
    </Card>
  );
};

/* ─────────────────────── easypay payout (promoters) ──────────────────────── */

/**
 * Two states, and only `easypayAccountId` decides which (see
 * utils/easypayPayout.js for why there is no third).
 *
 * The button used to say "Connect" and lead to Easypay's SIGNUP form, which is
 * what this fixes: a promoter who had already filled that form in was being
 * sent back to it, with nothing to do there and no way to find out where their
 * application had got to. It now opens their Easypay account instead. there2b
 * can't report on KYC — we learn nothing until an account id arrives — so the
 * honest move is to hand them the place that can.
 *
 * Ticking "I've created my Easypay account" does NOT soften this pill. It is a
 * claim we can't verify, and dressing it up as progress would leave a promoter
 * whose application stalled staring at reassuring amber for weeks.
 */
export const EasypayPayoutStatus = ({ promoter, t }) => {
  // The one link that stays useful in both states, and the only one a connected
  // promoter has: once the pitch card below disappears, this header is the only
  // place on there2b that still leads to their Easypay account — where the
  // balance, the payout history and the bank details actually live.
  const accessLink = (
    <a
      href={EASYPAY_LOGIN_URL}
      target="_blank"
      rel="noreferrer noopener"
      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-line-strong text-ink text-[12px] font-bold hover:bg-paper-2 transition-colors"
    >
      {t('easypay.open_cta')}
      <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
    </a>
  );

  if (hasEasypayPayoutAccount(promoter)) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 text-[12px] font-bold px-3 h-8">
          <CheckCircleIcon className="w-4 h-4" />
          {t('dashboard.payment_details_page.payout_connected_pill')}
        </span>
        {accessLink}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span
        className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-800 text-[12px] font-bold px-3 h-8"
        title={t('dashboard.payment_details_page.payout_pending_hint')}
      >
        <ExclamationTriangleIcon className="w-4 h-4" />
        {t('dashboard.payment_details_page.payout_pending_pill')}
      </span>
      <a
        href={EASYPAY_LOGIN_URL}
        target="_blank"
        rel="noreferrer noopener"
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-amber-300/70 text-amber-900 text-[12px] font-bold hover:bg-amber-50 transition-colors"
      >
        {t('easypay.open_cta')}
        <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
      </a>
    </div>
  );
};

/**
 * The full Easypay pitch, for a promoter who hasn't connected yet (T2B-423).
 *
 * Same content as registration section 05, because a promoter who ticked "I'd
 * rather do this later" at signup arrives here with none of that context and
 * an account that can't publish events. Disappears the moment there2b has
 * their account id — at which point the pill in the tab header says so and
 * there is nothing left to ask for.
 */
const EasypayConnectCard = ({ promoter, t }) => {
  const dispatch = useDispatch();
  const [howPaidOpen, setHowPaidOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const declared = hasDeclaredEasypaySignup(promoter);

  // One-way on purpose: this is there2b's cue to go and fetch their account id
  // from Easypay, and un-ticking it after we've already gone looking helps
  // nobody. Connecting is what makes the card — and the question — go away.
  const declareSignup = async () => {
    if (declared || saving) return;
    setSaving(true);
    try {
      await dispatch(
        updatePromoter({ easypayOnboardingChoice: EASYPAY_CHOICE.DECLARED })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader
        eyebrow={t('dashboard.payment_details_page.payout_eyebrow')}
        title={t('dashboard.payment_details_page.payout_title')}
        sub={
          <EasypayCopy
            i18nKey="dashboard.payment_details_page.payout_sub"
            as="span"
          />
        }
        action={
          <button
            type="button"
            onClick={() => setHowPaidOpen(true)}
            className="shrink-0 text-[13px] font-semibold text-ink underline underline-offset-4 hover:text-brand-deep"
          >
            {t('easypay.how_paid_link')}
          </button>
        }
      />

      <div className="flex flex-col gap-5">
        <PayoutJourney />
        <EasypayConnectButton showAccessLink />

        <div className="h-px bg-line" />

        {declared ? (
          <p className="m-0 flex items-start gap-2 text-[13px] leading-[1.5] text-muted">
            <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-good" />
            {t('dashboard.payment_details_page.payout_declared_note')}
          </p>
        ) : (
          <label className="flex cursor-pointer items-start gap-3 rounded-[12px] border border-line p-3.5 transition hover:bg-paper-2/50">
            <input
              type="checkbox"
              checked={false}
              disabled={saving}
              onChange={declareSignup}
              className="mt-0.5 h-[18px] w-[18px] shrink-0 accent-ink"
            />
            <span className="min-w-0">
              <span className="block text-[14px] font-semibold leading-snug text-ink-strong">
                {t('register.payouts_choice_declared_title')}
              </span>
              <span className="mt-0.5 block text-[12.5px] leading-[1.45] text-muted">
                {t('register.payouts_choice_declared_body')}
              </span>
            </span>
          </label>
        )}
      </div>

      <HowYouGetPaidModal
        isOpen={howPaidOpen}
        onClose={() => setHowPaidOpen(false)}
      />
    </Card>
  );
};

/* ───────────────────────── invoice row ───────────────────────────────────── */

const formatMoney = (amount, currency = 'EUR') =>
  new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(Number(amount || 0));

const InvoiceRow = ({ payment, isLast, onError, t }) => {
  const [downloading, setDownloading] = useState(false);
  const booking = payment.Booking || payment.booking;
  const event = booking?.Event || booking?.event;
  const bookingId = booking?.id;

  const reference = `#${payment.id}`;
  const date = payment.paymentDate
    ? moment(payment.paymentDate).format('D MMM YYYY')
    : '—';
  const grossAmount = payment.paidAmount ?? payment.amount;
  const amount = formatMoney(grossAmount, payment.currency || 'EUR');
  // Era-aware, keyed on when the BOOKING was priced rather than when it paid:
  // a booking created before the €50 fee ceiling shipped carries an uncapped
  // 5% fee in its total even if the capture happened afterwards.
  const t2bFee = formatMoney(
    computeT2bFeeInvoiceFromTotal(grossAmount, {
      at: booking?.createdAt || payment.paymentDate,
    }),
    payment.currency || 'EUR'
  );

  const statusLower = String(payment.status || '').toLowerCase();
  const isPaid =
    statusLower === 'paid' ||
    statusLower === 'completed' ||
    statusLower === 'success';

  const handleDownload = useCallback(async () => {
    if (!bookingId || downloading) return;
    setDownloading(true);
    try {
      const res = await api.get(`/bookings/${bookingId}/invoice/download`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `there2b-fatura-${bookingId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      onError?.(
        err?.response?.data?.message ||
          t('dashboard.payment_details_page.download_failed')
      );
    } finally {
      setDownloading(false);
    }
  }, [bookingId, downloading, onError, t]);

  const downloadable = !!bookingId && isPaid;

  return (
    <div
      className={`grid grid-cols-[1fr_auto] sm:grid-cols-[minmax(120px,1fr)_110px_110px_110px_110px_44px] items-center gap-3 px-5 sm:px-6 py-4 ${
        !isLast ? 'border-b border-line/70' : ''
      } hover:bg-paper-2 transition-colors`}
    >
      <div className="min-w-0">
        <div className="text-[12px] font-mono text-muted">{reference}</div>
        {event?.name && (
          <div
            className="text-[14px] font-bold text-ink-strong truncate"
            title={event.name}
          >
            {event.name}
          </div>
        )}
      </div>
      <div className="hidden sm:block text-[13px] text-ink">{date}</div>
      <div className="hidden sm:flex">
        <StatusPill status={payment.status} />
      </div>
      <div className="text-right sm:text-left text-[14px] font-bold font-mono text-ink-strong">
        {amount}
      </div>
      <div
        className="hidden sm:block text-[13px] font-mono text-ink"
        title={t('dashboard.payment_details_page.t2b_fee_info')}
      >
        {t2bFee}
      </div>
      <div className="col-span-2 sm:col-span-1 flex sm:justify-end justify-between items-center gap-2 sm:gap-0">
        {/* Mobile: show date + status here since columns collapse */}
        <div className="sm:hidden flex items-center gap-2 text-[12px] text-muted">
          <span>{date}</span>
          <StatusPill status={payment.status} />
        </div>
        <button
          type="button"
          onClick={handleDownload}
          disabled={!downloadable || downloading}
          title={
            downloadable
              ? t('dashboard.payment_details_page.download_invoice')
              : t('dashboard.payment_details_page.download_unavailable')
          }
          className="w-9 h-9 inline-flex items-center justify-center rounded-full hover:bg-paper-2 text-ink disabled:text-muted disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors"
        >
          {downloading ? (
            <ClipLoader color="#1A1A1F" size={14} />
          ) : (
            <ArrowDownTrayIcon className="w-[18px] h-[18px]" />
          )}
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────────── PaymentDetails ──────────────────────────────── */

const PaymentDetails = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingIdFilter = searchParams.get('bookingId');

  const { paymentsLoading, payments, error } = useSelector((s) => s.payments);
  const authUser = useSelector((s) => s.auth.user);
  const promoter = useSelector((s) => s.promoter.promoter);
  // Same answer the dimmed "Create event" buttons give, so the card that
  // explains the block and the block itself can't disagree.
  const easypayGate = useEasypayGate();
  const isBrand = authUser?.role === 'brand';

  const [downloadError, setDownloadError] = useState(null);

  useEffect(() => {
    const params = { page: 1, pageSize: 50 };
    if (bookingIdFilter) params.bookingId = bookingIdFilter;
    dispatch(fetchPayments(params));
  }, [dispatch, bookingIdFilter]);

  const handleNavigateToInfo = () => {
    // Switch to the "info" tab — Dashboard reads ?tab= on URL change.
    const sp = new URLSearchParams(window.location.search);
    sp.set('tab', 'info');
    navigate(`/dashboard?${sp.toString()}`);
  };

  const list = Array.isArray(payments) ? payments : [];

  return (
    <div className="space-y-6">
      <TabHeader
        eyebrow={t('dashboard.payment_details_page.eyebrow')}
        title={t('dashboard.payment_details_page.title')}
        sub={t('dashboard.payment_details_page.subtitle')}
        actions={
          isBrand ? null : <EasypayPayoutStatus promoter={promoter} t={t} />
        }
      />

      {/* Billing details — editable in place for brands (they're the ones who
          get invoiced); promoters' company data still lives in Main Info. */}
      {easypayGate.blocked && (
        <EasypayConnectCard promoter={easypayGate.promoter} t={t} />
      )}

      {isBrand ? (
        <BrandBillingCard t={t} />
      ) : (
        <PromoterBillingCard
          promoter={promoter}
          t={t}
          onNavigateToInfo={handleNavigateToInfo}
        />
      )}

      {/* History */}
      <Card padded={false}>
        <div className="p-6 pb-4">
          <CardHeader
            eyebrow={t('dashboard.payment_details_page.history_eyebrow')}
            title={t('dashboard.payment_details_page.history_title')}
          />
          {downloadError && (
            <div className="mb-3 rounded-xl bg-rose-50 border border-rose-100 px-3 py-2.5 text-[13px] text-rose-700">
              {downloadError}
            </div>
          )}
        </div>

        {paymentsLoading ? (
          <div className="flex items-center justify-center py-12">
            <ClipLoader color="#FFBD59" size={32} />
          </div>
        ) : error ? (
          <div className="px-6 pb-6">
            <div className="rounded-xl bg-rose-50 border border-rose-100 px-3 py-2.5 text-[13px] text-rose-700">
              {error}
            </div>
          </div>
        ) : list.length === 0 ? (
          <div className="py-10 px-6 text-center">
            <div className="w-12 h-12 rounded-2xl bg-brand-softer text-yellow-03 mx-auto mb-3 flex items-center justify-center">
              <DocumentTextIcon className="w-6 h-6" />
            </div>
            <h3 className="text-[16px] font-bold text-ink-strong">
              {t('dashboard.payment_details_page.history_empty_title')}
            </h3>
            <p className="text-[13px] text-muted mt-1 max-w-md mx-auto">
              {t('dashboard.payment_details_page.history_empty_sub')}
            </p>
          </div>
        ) : (
          <>
            {/* Table header (desktop only) */}
            <div className="hidden sm:grid grid-cols-[minmax(120px,1fr)_110px_110px_110px_110px_44px] gap-3 px-6 pb-2 text-[10.5px] uppercase tracking-[0.08em] text-muted font-bold">
              <div>
                {t('dashboard.payment_details_page.col_reference')}
              </div>
              <div>{t('dashboard.payment_details_page.col_date')}</div>
              <div>{t('dashboard.payment_details_page.col_status')}</div>
              <div className="flex items-center gap-1">
                <span>{t('dashboard.payment_details_page.col_amount')}</span>
                <span
                  title={t('dashboard.payment_details_page.col_amount_info')}
                  className="cursor-help"
                >
                  <InformationCircleIcon className="w-3.5 h-3.5" />
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span>{t('dashboard.payment_details_page.col_t2b_fee')}</span>
                <span
                  title={t('dashboard.payment_details_page.t2b_fee_info')}
                  className="cursor-help"
                >
                  <InformationCircleIcon className="w-3.5 h-3.5" />
                </span>
              </div>
              <div className="text-right">·</div>
            </div>
            <div>
              {list.map((p, i) => (
                <InvoiceRow
                  key={p.id}
                  payment={p}
                  isLast={i === list.length - 1}
                  onError={setDownloadError}
                  t={t}
                />
              ))}
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default PaymentDetails;
